import { useState, useEffect } from 'react';
import { getDatabase, ref, onValue, query, orderByChild, equalTo, get } from 'firebase/database';
import { getAuth } from 'firebase/auth';

// Hook für einzelne Product-Daten
export const useProductData = (productId, forceRefresh = 0) => {
    const [productData, setProductData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        console.log('🚀 useProductData - Effect triggered for productId:', productId);
        
        if (!productId) {
            console.log('useProductData - No productId provided, resetting state');
            setProductData(null);
            setLoading(false);
            setError(null);
            return;
        }

        // Reset states when productId changes
        console.log('🔄 useProductData - Resetting states for:', productId);
        setLoading(true);
        setError(null);
        // Force clear previous productData to ensure fresh state
        setProductData(null);

        const db = getDatabase();
        const productRef = ref(db, `products/${productId}`);
        console.log('🎯 useProductData - Setting up NEW listener for:', productRef.toString());

        // Force immediate read first, then listen for changes
        console.log('📖 useProductData - Forcing immediate read for:', productId);
        
        // Immediate read to get latest data
        get(productRef).then((snapshot) => {
            console.log('⚡ useProductData - Immediate read completed for:', productId);
            if (snapshot.exists()) {
                const immediateData = { id: productId, ...snapshot.val() };
                console.log('⚡ useProductData - Immediate data:', immediateData);
                setProductData(immediateData);
                setError(null);
            } else {
                setProductData(null);
                setError('Product not found');
            }
            setLoading(false);
        }).catch((error) => {
            console.error('⚡ useProductData - Immediate read error:', error);
            setError('Failed to load product');
            setLoading(false);
        });

        const unsubscribe = onValue(productRef, 
            (snapshot) => {
                console.log('🔥 useProductData - Firebase callback triggered for:', productId, {
                    exists: snapshot.exists(),
                    timestamp: new Date().toISOString(),
                    currentLoading: loading
                });
                
                if (snapshot.exists()) {
                    const rawData = snapshot.val();
                    const newProductData = { id: productId, ...rawData };
                    
                    console.log('✅ useProductData - Firebase data received:', productId);
                    console.log('📊 useProductData - Raw Firebase data:', rawData);
                    console.log('💰 useProductData - Processed product data:', {
                        id: newProductData.id,
                        title: newProductData.title,
                        price: newProductData.price,
                        currency: newProductData.currency,
                        showDiscount: newProductData.showDiscount,
                        discountType: newProductData.discountType,
                        discountValue: newProductData.discountValue,
                        discountTimeLimited: newProductData.discountTimeLimited,
                        discountExpiryDate: newProductData.discountExpiryDate,
                        discountLabel: newProductData.discountLabel,
                        updatedAt: newProductData.updatedAt,
                        timestamp: new Date().toISOString()
                    });
                    
                    setProductData(newProductData);
                    setError(null);
                    console.log('✅ useProductData - State updated successfully for:', productId);
                } else {
                    console.log('❌ useProductData - Product not found:', productId);
                    setProductData(null);
                    setError('Product not found');
                }
                
                setLoading(false);
                console.log('🏁 useProductData - Loading set to false for:', productId);
            },
            (error) => {
                console.error('useProductData - Firebase error for product:', productId, error);
                setError('Failed to load product');
                setLoading(false);
            }
        );

        return () => {
            console.log('🧹 useProductData - Cleaning up listener for:', productId);
            unsubscribe();
        };
    }, [productId, forceRefresh]); // Include forceRefresh in dependencies

    return { productData, loading, error };
};

// Hook für alle User-Produkte (für Product-Selektor)
export const useUserProducts = () => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const auth = getAuth();
    const currentUser = auth.currentUser;

    useEffect(() => {
        if (!currentUser) {
            setProducts([]);
            setLoading(false);
            return;
        }

        const db = getDatabase();
        const productsQuery = query(
            ref(db, 'products'),
            orderByChild('providerId'),
            equalTo(currentUser.uid)
        );

        const unsubscribe = onValue(productsQuery,
            (snapshot) => {
                const productsData = [];
                snapshot.forEach((childSnapshot) => {
                    productsData.push({
                        id: childSnapshot.key,
                        ...childSnapshot.val()
                    });
                });
                
                // Sortiere nach Erstellungsdatum (neueste zuerst)
                productsData.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
                
                setProducts(productsData);
                setError(null);
                setLoading(false);
            },
            (error) => {
                console.error('Error loading user products:', error);
                setError('Failed to load products');
                setLoading(false);
            }
        );

        return () => unsubscribe();
    }, [currentUser]);

    return { products, loading, error };
};

// Helper function für Product-Status
export const getProductStatusInfo = (status) => {
    const statusConfig = {
        draft: { 
            label: 'Draft', 
            className: 'status-draft', 
            color: '#6b7280',
            icon: '📝' 
        },
        published: { 
            label: 'Published', 
            className: 'status-published', 
            color: '#10b981',
            icon: '✅' 
        },
        archived: { 
            label: 'Archived', 
            className: 'status-archived', 
            color: '#f59e0b',
            icon: '📦' 
        }
    };
    
    return statusConfig[status] || statusConfig.draft;
};

// Helper function für Product-Type Icons
export const getProductTypeInfo = (type) => {
    const typeConfig = {
        digital: {
            label: 'Digital Product',
            icon: '📱',
            color: '#667eea'
        },
        course: {
            label: 'Video Course', 
            icon: '🎥',
            color: '#10b981'
        },
        event: {
            label: 'Live Event',
            icon: '🎟️', 
            color: '#f59e0b'
        }
    };
    
    return typeConfig[type] || typeConfig.digital;
};
